import { Spin } from "antd"
import { ISapAsset } from "@/types/sapAsset.type"
import { IPaginationMeta } from "@/types/paginationParam.type"

import moment from "moment"
import { useEffect, useRef } from "react"
import { useNavigate } from "react-router"
import EmptyData from "@/components/common/EmptyData"
import { VariantProps } from "class-variance-authority"
import { RibbonCard } from "@/components/ui/ribbon-card"

interface Props {
  assets?: ISapAsset[]
  isLoading?: boolean
  handleReset?: () => void
  onLoadMore?: () => void
  meta?: IPaginationMeta
  hasMore?: boolean
}

const AssetCard = ({ assets, isLoading, handleReset, onLoadMore, hasMore = true }: Props) => {
  const observerTarget = useRef(null)
  const loadingRef = useRef(false)
  const navigate = useNavigate()

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !loadingRef.current) {
          loadingRef.current = true
          onLoadMore?.()
        }
      },
      { threshold: 0.1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [hasMore, isLoading, onLoadMore])

  useEffect(() => {
    if (!isLoading) {
      loadingRef.current = false
    }
  }, [isLoading])

  if (assets?.length === 0 && !isLoading) {
    return <EmptyData handleReset={handleReset} />
  }

  const renderStatus = (status: string): VariantProps<typeof RibbonCard>["variant"] => {
    switch (status) {
      case "Waiting Approval":
        return "warning"
      case "Approved":
        return "success"
      case "Reject":
      case "Cancel":
        return "error"
      default:
        return "black"
    }
  }

  return (
    <Spin spinning={isLoading} style={{ marginTop: 16 }}>
      {/* Card List */}
      <div className="md:space-y-6 space-y-4">
        {assets?.map(asset => (
          <RibbonCard key={asset.record_id} position="bottom-right" variant={renderStatus(asset.approval_status)} ribbon={asset.approval_status}>
            <div className="flex flex-col justify-between md:gap-3 gap-2" onClick={() => navigate(`/sapasst/view/${asset.record_id}`)}>
              <div className="flex flex-col">
                <p className="md:text-sm text-xs font-semibold">{asset.fullname}</p>
                <span className="md:text-xs text-[10px] font-medium">Record ID: {asset.record_id}</span>
              </div>

              <span className="text-xs text-gray-500 ">{moment(asset.updated_at).format("LLL")}</span>
            </div>
          </RibbonCard>
        ))}
      </div>

      {/* Infinite Scroll Trigger */}
      <div ref={observerTarget} style={{ height: 20, marginTop: 16 }} />

      {isLoading && (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Spin />
        </div>
      )}

      {!hasMore && assets && assets.length > 0 && <div style={{ textAlign: "center", padding: "20px 0", color: "#8c8c8c" }}>No more items to load</div>}
    </Spin>
  )
}

export default AssetCard
